import * as React from "react";
import { useMemo, useContext } from "react";
import * as Immutable from "immutable";
import { useQuery } from "@tanstack/react-query";
import { Table } from "components/bootstrap";
import { Spinner } from "components/common";
import HelpPanel from "components/event-definitions/common/HelpPanel";
import useCurrentUser from "hooks/useCurrentUser";
import generateId from "logic/generateId";
import useSearchExecutors from "views/components/contexts/useSearchExecutors";
import Search from "views/logic/search/Search";
import createSearch from "views/logic/slices/createSearch";
import SearchExecutionState from "views/logic/search/SearchExecutionState";
import Query from "views/logic/queries/Query";
import Parameter from "views/logic/parameters/Parameter";
import { isPermitted } from "util/PermissionsMixin";
import FilterPreviewResults from "components/event-definitions/event-definition-types/FilterPreviewResults";
import useDebouncedValue from "hooks/useDebouncedValue";
import StreamsContext from "contexts/StreamsContext";
const Messages = ({ messages }) => messages.map(({ index, message }) => /* @__PURE__ */ React.createElement("tr", { key: `${index}-${message._id}` }, /* @__PURE__ */ React.createElement("td", null, message.timestamp), /* @__PURE__ */ React.createElement("td", null, message.message)));
const SearchResult = ({
  searchResult,
  isFetchingData
}) => {
  if (isFetchingData) return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading filter preview..." });
  if (!searchResult.messages || searchResult.messages.length === 0) {
    return /* @__PURE__ */ React.createElement("p", null, "Could not find any messages with the current search criteria.");
  }
  return /* @__PURE__ */ React.createElement(Table, { condensed: true, bordered: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Timestamp"), /* @__PURE__ */ React.createElement("th", null, "Message"))), /* @__PURE__ */ React.createElement("tbody", null, /* @__PURE__ */ React.createElement(Messages, { messages: searchResult.messages })));
};
const constructSearch = (config, searchTypeId, queryId, streams) => {
  var _a, _b, _c;
  const combinedStreamsAndCategories = /* @__PURE__ */ new Set([
    ...(config == null ? void 0 : config.streams) || [],
    ...((_a = config == null ? void 0 : config.stream_categories) == null ? void 0 : _a.flatMap(
      (category) => streams.filter((s) => s.categories.includes(category)).map((s) => s.id)
    )) || []
  ]);
  const formattedStreams = [...combinedStreamsAndCategories].map((stream) => ({ type: "stream", id: stream })) || [];
  const queryBuilder = Query.builder().id(queryId).query({ type: "elasticsearch", query_string: (config == null ? void 0 : config.query) || "*" }).timerange({ type: "relative", range: ((config == null ? void 0 : config.search_within_ms) || 0) / 1e3 }).filter(formattedStreams.length === 0 ? null : Immutable.Map({ type: "or", filters: formattedStreams })).filters(config.filters).searchTypes([
    {
      id: searchTypeId,
      type: "messages",
      limit: 10,
      offset: 0,
      filter: void 0,
      filters: void 0,
      name: void 0,
      query: void 0,
      timerange: void 0,
      streams: [],
      stream_categories: [],
      sort: [],
      decorators: []
    }
  ]);
  const query = queryBuilder.build();
  return Search.create().toBuilder().parameters(
    (_c = (_b = config == null ? void 0 : config.query_parameters) == null ? void 0 : _b.filter((param) => !param.embryonic).map((param) => Parameter.fromJSON(param))) != null ? _c : []
  ).queries([query]).build();
};
const isPermittedToSeePreview = (currentUser, config) => {
  var _a;
  const missingPermissions = (_a = config == null ? void 0 : config.streams) == null ? void 0 : _a.some(
    (stream) => !isPermitted(currentUser.permissions, `streams:read:${stream}`)
  );
  return !missingPermissions;
};
const useExecutePreview = (config) => {
  var _a, _b, _c, _d, _e;
  const streams = useContext(StreamsContext);
  const currentUser = useCurrentUser();
  const queryId = useMemo(() => generateId(), []);
  const searchTypeId = useMemo(() => generateId(), []);
  const { startJob, executeJobResult } = useSearchExecutors();
  const permitted = isPermittedToSeePreview(currentUser, config);
  const [debouncedConfig] = useDebouncedValue(config, 250);
  const hasEmbryonicParams = (_a = debouncedConfig == null ? void 0 : debouncedConfig.query_parameters) == null ? void 0 : _a.some(
    (param) => param.embryonic
  );
  const { data, isFetching } = useQuery({
    queryKey: ["filter-preview", debouncedConfig, searchTypeId, queryId, streams],
    queryFn: async () => {
      const search = constructSearch(debouncedConfig, searchTypeId, queryId, streams);
      const createdSearch = await createSearch(search);
      const jobIds = await startJob(createdSearch, [searchTypeId], SearchExecutionState.empty());
      const result = await executeJobResult({ jobIds });
      return result;
    },
    enabled: permitted && !hasEmbryonicParams,
    retry: false
  });
  return {
    isFetching,
    errors: (_b = data == null ? void 0 : data.result) == null ? void 0 : _b.errors,
    result: (_e = (_d = (_c = data == null ? void 0 : data.result) == null ? void 0 : _c.forId(queryId)) == null ? void 0 : _d.searchTypes) == null ? void 0 : _e[searchTypeId]
  };
};
const FilterPreview = ({ config }) => {
  const currentUser = useCurrentUser();
  const displayPreview = isPermittedToSeePreview(currentUser, config);
  const { isFetching, result: searchResult = {}, errors = [] } = useExecutePreview(config);
  const isFetchingData = isFetching || !searchResult;
  const hasError = (errors == null ? void 0 : errors.length) > 0;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    HelpPanel,
    {
      collapsible: true,
      defaultExpanded: !displayPreview,
      title: "How many Events will Filter & Aggregation create?"
    },
    /* @__PURE__ */ React.createElement("p", null, "The Filter & Aggregation Condition will generate different number of Events, depending on how it is configured:"),
    /* @__PURE__ */ React.createElement("ul", null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("b", null, "Filter:"), "\u2003One Event per message matching the filter"), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("b", null, "Aggregation without groups:"), "\u2003One Event every time the aggregation result satisfies the condition"), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("b", null, "Aggregation with groups:"), "\u2003One Event per group whose aggregation result satisfies the condition"))
  ), displayPreview && /* @__PURE__ */ React.createElement(FilterPreviewResults, { hasError }, hasError ? /* @__PURE__ */ React.createElement("p", null, errors[0].description) : /* @__PURE__ */ React.createElement(SearchResult, { isFetchingData, searchResult })));
};
export default FilterPreview;
